/* eslint-disable no-unused-vars */
import Vue from "vue";
import axios from "axios";
import moment from "moment";
import _ from "lodash";
import palette from "google-palette";
import * as url from "url";

let state = {
  clientIdFilter: null,
  manufacturers: [],
  materials: [],
  collections: [],
  problemCategories: [],
  defectsData: [],
  start_date: null,
  end_date: null,
  unit: null,
  loading: {
    manufacturers: false,
    collections: false,
    problemCategories: false,
    materials: false,
    monthlyExports: false,
    manufacturerStats: false,
    passRates: false
  },
  monthlyExports: [],
  passRates: [],
  manufacturerStats: [],
  tallyStats: []
};

const mutations = {
  setDates(state, dateRange) {
    state.start_date = dateRange.start_date;
    state.end_date = dateRange.end_date;
    state.unit = dateRange.unit;
  },
  setManufacturers(state, manufacturers) {
    state.manufacturers = manufacturers;
  },
  setCollections(state, collections) {
    state.collections = collections;
  },
  setProblemCategories(state, problemCategories) {
    state.problemCategories = problemCategories;
  },
  setMaterials(state, materials) {
    state.materials = materials;
  },
  setLoading(state, data) {
    state.loading[data.type] = data.value;
  },
  setClientFilter(state, id) {
    state.clientIdFilter = id;
  }
};

const actions = {
  getManufacturers: _.debounce(function({ state, commit }, filters) {
    commit("setLoading", { type: "manufacturers", value: true });
    axios
      .get("/api/analytics/manufacturers", {
        params: {
          ...filters,
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        commit("setManufacturers", r.data.data);
      })
      .finally(() => {
        commit("setLoading", { type: "collections", value: true });
      });
  }, 500),
  getCollections: _.debounce(function({ state, commit }, filters) {
    commit("setLoading", { type: "collections", value: true });
    axios
      .get("/api/analytics/collections", {
        params: {
          ...filters,
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        commit("setCollections", r.data.data);
      })
      .finally(() => {
        commit("setLoading", { type: "collections", value: false });
      });
  }, 500),
  getProblemCategories: _.debounce(function({ state, commit }, filters) {
    commit("setLoading", { type: "problemCategories", value: true });
    axios
      .get("/api/analytics/problem-categories", {
        params: {
          ...filters,
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        commit("setProblemCategories", r.data.data);
      })
      .finally(() => {
        commit("setLoading", { type: "problemCategories", value: false });
      });
  }, 500),
  getMaterials: _.debounce(function({ state, commit }, filters) {
    commit("setLoading", { type: "materials", value: true });
    axios
      .get("/api/analytics/product-attribute-options", {
        params: {
          ...filters,
          attribute: "materials",
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        commit("setMaterials", r.data.data);
      })
      .finally(() => {
        commit("setLoading", { type: "materials", value: false });
      });
  }, 500),
  getMonthlyExports({ state, commit, getters }, filters) {
    commit("setLoading", { type: "monthlyExports", value: true });
    axios
      .get("/api/analytics/total-monthly-exports", {
        params: { ...filters, client_id: state.clientIdFilter }
      })
      .then(r => {
        state.monthlyExports = r.data.data;
      })
      .finally(() => {
        commit("setLoading", { type: "monthlyExports", value: false });
      });
  },
  getPassRates({ state, commit, getters }, filters) {
    commit("setLoading", { type: "passRates", value: true });
    axios
      .get("/api/analytics/pass-rates", {
        params: {
          ...filters,
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        state.passRates = r.data.data;
      })
      .finally(() => {
        commit("setLoading", { type: "passRates", value: false });
      });
  },
  getDefectsData({ state, commit, getters }, filters) {
    commit("setLoading", { type: "defects", value: true });

    let url = "/api/analytics/defect-categories";
    if (filters.category) {
      url += "/" + filters.category;
    }

    axios
      .get(url, {
        params: {
          client_id: state.clientIdFilter,
          base_product: filters.base_product,
          product_variant: filters.product_variant,
          start_date: filters.start_date,
          end_date: filters.end_date,
          unit: filters.unit
        }
      })
      .then(r => {
        state.defectsData = r.data.data;
      })
      .finally(() => {
        commit("setLoading", { type: "defects", value: false });
      });
  },
  getManufacturerStats({ state, commit, getters }, timeframe) {
    commit("setLoading", { type: "manufacturerStats", value: true });
    axios
      .get("/api/analytics/manufacturer-stats" + timeframe, {
        params: {
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        state.manufacturerStats = r.data;
      })
      .finally(() => {
        commit("setLoading", { type: "manufacturerStats", value: false });
      });
  },
  getTallyStats({ state, commit }, filters) {
    commit("setLoading", { type: "tallyStats", value: true });
    return axios
      .get("/api/analytics/tally", {
        params: {
          ...filters,
          client_id: state.clientIdFilter
        }
      })
      .then(r => {
        state.tallyStats = r.data;
      })
      .finally(() => {
        commit("setLoading", { type: "tallyStats", value: false });
      });
  }
};

const getters = {
  getManufacturersForFormSelect: state => {
    return state.manufacturers.map(m => {
      return { text: m.name, value: m.id };
    });
  },
  getCollectionsForFormSelect: state => {
    return state.collections.map(m => {
      return { text: m.name + " (" + m.client.name + ")", value: m.id };
    });
  },
  getMaterialsForFormSelect: state => {
    return state.materials.map(m => {
      return { text: m.value, value: m.id };
    });
  },
  getProblemCategoriesForFormSelect: state => {
    return state.problemCategories.map(m => {
      return { text: m.name, value: m.id };
    });
  },
  getMonthlyExportsChartData: state => {
    return {
      labels: state.monthlyExports.map(m => {
        return m.text;
      }),
      datasets: [
        {
          label: "Pass",
          backgroundColor: "#2ECCCC",
          data: state.monthlyExports.map(m => {
            return m.passed_quantity;
          })
        },
        {
          label: "Saved",
          backgroundColor: "#FFCC63",
          data: state.monthlyExports.map(m => {
            return m.saved_quantity;
          })
        }
      ]
    };
  },
  getPassRateChartData: state => {
    let labels = [];
    state.passRates.forEach(r => {
      if (!labels.find(l => r.text === l)) {
        labels.push(r.text);
      }
    });

    let datasets = [];
    state.passRates.forEach((r, k) => {
      let set = datasets.find(d => d.label === r.label);
      if (!set) {
        let color = palette("tol", state.passRates.length).map(function(hex) {
          return "#" + hex;
        })[k];
        set = {
          label: r.label,
          data: [],
          borderColor: color,
          backgroundColor: color,
          fill: false,
          lineTension: 0,
          pointRadius: 10,
          pointHoverRadius: 15,
          spanGaps: true
        };
        datasets.push(set);
      }
    });

    datasets.forEach(d => {
      labels.forEach(l => {
        let p = state.passRates.find(
          pr => pr.text === l && pr.label === d.label
        );
        d.data.push(p ? p.pass_rate : null);
      });
    });

    return {
      labels: labels,
      datasets: datasets
    };
  },
  getDefectsChartData: state => {
    return {
      labels: state.defectsData.map(d => d.name),
      datasets: [
        {
          data: state.defectsData.map(d => d.count),
          backgroundColor: palette("tol-rainbow", state.defectsData.length).map(
            h => "#" + h
          )
        }
      ]
    };
  },
  getManufacturerStatsData: state => {
    return state.manufacturerStats;
  },
  getTallyStatsData: state => {
    return state.tallyStats;
  }
};

function getUrlParams() {
  let uri = window.location.href.split("?");
  if (uri.length == 2) {
    // let vars = uri[1].split('&');
    // let getVars = {};
    // let tmp = '';
    // vars.forEach(function(v){
    //   tmp = v.split('=');
    //   if(tmp.length == 2)
    //     getVars[tmp[0]] = tmp[1];
    // });
    return "?" + uri[1];
  } else {
    return "";
  }
}

export default {
  state,
  mutations,
  actions,
  getters,
  namespaced: true
};
